import React, { useState, useCallback, useEffect } from 'react';
import { unstable_usePrompt } from 'react-router-dom';
import { sortBy, isEqual } from 'lodash';
import Sortable from 'react-sortablejs';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '@store/policies/precisionPolicy/actions';
import { selector } from '@store/policies/precisionPolicy';
import { ViewTypes } from '@store/policies/precisionPolicy/reducers';
import { IPolicy, IResource } from '../../types';
import { Wrapper } from './styled';
import Buttons from './Buttons';
import { LEAVE_CONFIRMATION } from '../../constants';
import ScheduleList from '../ScheduleList';
import { isExpandedViewSelector } from '@store/policies/precisionPolicy/selectors';
import CollapsedView from './CollapsedView';
import ExpandedView from './ExpandedView';
import ReorderScheduleModal from '../ScheduleModal/ReorderScheduleModal';
import {
  setOrder as setOrderInStore,
  setVisible,
  setModalType,
} from '@src/store/policies/schedule/actions';
import { policyAction } from '@src/store/policies/schedule/utils';
import { formViewSelector } from '@store/policies/schedule/selectors';
import ScheduleModal from '../ScheduleModal/index';
interface IProps {
  policies: IPolicy[];
  resources: IResource[];
  canEdit?: boolean;
  activeView: ViewTypes;
}

const getRenderedPoliciesInExpandedView = ({
  policies,
  sortable,
  resources,
  canEdit,
  order,
  originalOrder,
}) =>
  policies.map((policy, idx) => (
    <ExpandedView
      key={idx}
      policy={policy}
      idx={idx}
      sortable={sortable}
      resources={resources}
      canEdit={canEdit}
      order={order}
      originalOrder={originalOrder}
    />
  ));

const getRenderedPoliciesInCollapsedView = ({
  policies,
  sortable,
  resources,
  canEdit,
  order,
  originalOrder,
}) =>
  policies.map((policy, idx) => (
    <CollapsedView
      policy={policy}
      idx={idx}
      key={idx}
      sortable={sortable}
      resources={resources}
      canEdit={canEdit}
      order={order}
      originalOrder={originalOrder}
    />
  ));

const Policies: React.FC<IProps> = ({
  policies: unOrderedPolicies,
  resources,
  canEdit = false,
  activeView,
}) => {
  const sortedPolices = sortBy(unOrderedPolicies, (p) => p.index);
  const originalOrder = sortedPolices.map((p) => p.id);
  const [order, setOrder] = useState(sortedPolices.map((p) => p.id));
  const { sortable } = useSelector(selector);
  const dispatch = useDispatch();
  const policies = sortBy(unOrderedPolicies, (p) => order.indexOf(p.id));
  const isExpandedView = useSelector(isExpandedViewSelector);

  const form: any = useSelector(formViewSelector);
  const formOrder = form.order;

  const onCancel = useCallback(() => {
    setOrder(originalOrder);
    dispatch(actions.toggleSortable(false));
  }, [dispatch, originalOrder]);

  const onToggle = useCallback(() => {
    dispatch(actions.toggleSortable(true));
  }, [dispatch]);

  const onOk = useCallback(() => {
    // setModalVisible(true);
    dispatch(setModalType(policyAction.reorder));
    dispatch(setVisible(true));
  }, [dispatch]);

  const onLayoutChange = useCallback((newOrder) => {
    const updatedOrder = newOrder.map((o) => parseInt(o, 10));
    setOrder(updatedOrder);
  }, []);

  useEffect(() => {
    if (sortable) {
      dispatch(setOrderInStore(order));
    }
  }, [order, formOrder, dispatch, sortable]);

  const renderedPoliciesInExpandedView = getRenderedPoliciesInExpandedView({
    policies,
    sortable,
    resources,
    canEdit,
    order,
    originalOrder,
  });

  const renderedPoliciesInCollapsedView = getRenderedPoliciesInCollapsedView({
    policies,
    sortable,
    resources,
    canEdit,
    order,
    originalOrder,
  });

  // TODO: migrate to useBlocker
  unstable_usePrompt({
    when:!isEqual(order, originalOrder),
    message:`${LEAVE_CONFIRMATION}`,
  });

  return (
    <>
      <Wrapper>
        <Buttons
          editMode={sortable}
          onOk={onOk}
          onCancel={onCancel}
          onToggle={onToggle}
          canEdit={canEdit}
          activeView={activeView}
        />
        {activeView === ViewTypes.policies && (
          <Sortable
            key={`Sortable-Policies-${sortable}`}
            options={{
              disabled: !sortable,
              ghostClass: 'ghost-version',
            }}
            onChange={onLayoutChange}
          >
            {isExpandedView ? renderedPoliciesInExpandedView : renderedPoliciesInCollapsedView}
          </Sortable>
        )}
        {activeView === ViewTypes.schedules && <ScheduleList canEdit={canEdit} />}
        <ReorderScheduleModal />
        <ScheduleModal canEdit={canEdit} showButton={false} />
      </Wrapper>
    </>
  );
};

export default Policies;
